import React, { useState, useEffect} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Alert,
} from "reactstrap";
import Select from "react-select";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axiosInstance from "../../helpers/axiosInstance";
import gsap from "gsap";
import { toast } from "react-toastify";

const QuantityRestriction = () => {
  const [restrictions, setRestrictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRestrictions, setTotalRestrictions] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRestrictions, setSelectedRestrictions] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Modal states
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRestriction, setSelectedRestriction] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    maxQuantity: "",
    productIds: [],
    validUntil: "",
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});

  const [breadcrumbItems] = useState([
    { title: "Ecommerce", link: "#" },
    { title: "Quantity Restrictions", link: "#" },
  ]);

  useEffect(() => {
    fetchRestrictions(1, searchTerm);
    fetchProducts();
  }, []);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const fetchRestrictions = async (page = 1, name = "") => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `api/v1/quantity-restriction/admin/quantity-restrictions?page=${page}&limit=50${name ? `&name=${name}` : ''}`
      );
      setRestrictions(response.data.quantityRestrictions);
      setTotalRestrictions(response.data.total || response.data.length);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching restrictions:", error);
      toast.error("Failed to load quantity restrictions. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get("api/v1/products?limit=500000");
      setProducts(response.data.products || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleSearchSubmit = () => {
    setRestrictions([]);
    setCurrentPage(1);
    fetchRestrictions(1, searchTerm);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      if (value.trim()) {
        setRestrictions([]);
        setCurrentPage(1);
        fetchRestrictions(1, value);
      } else if (value === "") {
        // If search is cleared, fetch all restrictions
        setRestrictions([]);
        setCurrentPage(1);
        fetchRestrictions(1, "");
      }
    }, 500); // 500ms delay
    
    setSearchTimeout(timeout);
  };

  const handleCheckboxChange = (restrictionId) => {
    let updatedSelectedRestrictions;
    if (selectedRestrictions.includes(restrictionId)) {
      updatedSelectedRestrictions = selectedRestrictions.filter(
        (id) => id !== restrictionId
      );
    } else {
      updatedSelectedRestrictions = [...selectedRestrictions, restrictionId];
    }
    setSelectedRestrictions(updatedSelectedRestrictions);
    setShowBulkDelete(updatedSelectedRestrictions.length > 0);
  };

  const handleSelectAll = () => {
    if (selectedRestrictions.length === restrictions.length) {
      setSelectedRestrictions([]);
      setShowBulkDelete(false);
    } else {
      setSelectedRestrictions(restrictions.map((r) => r._id));
      setShowBulkDelete(true);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      resetForm();
    }
  };

  const toggleEditModal = (restriction = null) => {
    setEditModal(!editModal);
    if (restriction) {
      setSelectedRestriction(restriction);
      setFormData({
        name: restriction.name || "",
        description: restriction.description || "",
        maxQuantity: restriction.maxQuantity || "",
        productIds: restriction.productIds || [],
        validUntil: restriction.validUntil ? new Date(restriction.validUntil).toISOString().slice(0, 16) : "",
        isActive: restriction.isActive !== undefined ? restriction.isActive : true
      });
    } else {
      resetForm();
    }
  };

  const toggleDeleteModal = (restriction = null) => {
    setDeleteModal(!deleteModal);
    setSelectedRestriction(restriction);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      maxQuantity: "",
      productIds: [],
      validUntil: "",
      isActive: true
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    
    if (!formData.maxQuantity || formData.maxQuantity <= 0) {
      errors.maxQuantity = "Max quantity must be greater than 0";
    }
    
    if (!formData.productIds || formData.productIds.length === 0) {
      errors.productIds = "At least one product must be selected";
    }
    
    if (!formData.validUntil) {
      errors.validUntil = "Valid until date is required";
    } else if (new Date(formData.validUntil) <= new Date()) {
      errors.validUntil = "Valid until date must be in the future";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        maxQuantity: parseInt(formData.maxQuantity),
        validUntil: new Date(formData.validUntil).toISOString()
      };

      await axiosInstance.post("api/v1/quantity-restriction/admin/quantity-restriction/new", payload);
      toast.success("Quantity restriction created successfully");
      toggleModal();
      fetchRestrictions(currentPage, searchTerm);
    } catch (error) {
      console.error("Error creating restriction:", error);
      toast.error(error.response?.data?.message || "Failed to create quantity restriction");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        maxQuantity: parseInt(formData.maxQuantity),
        validUntil: new Date(formData.validUntil).toISOString()
      };

      await axiosInstance.put(`api/v1/quantity-restriction/admin/quantity-restriction/${selectedRestriction._id}`, payload);
      toast.success("Quantity restriction updated successfully");
      toggleEditModal();
      fetchRestrictions(currentPage, searchTerm);
    } catch (error) {
      console.error("Error updating restriction:", error);
      toast.error(error.response?.data?.message || "Failed to update quantity restriction");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await axiosInstance.delete(`api/v1/quantity-restriction/admin/quantity-restriction/${selectedRestriction._id}`);
      toast.success("Quantity restriction deleted successfully");
      toggleDeleteModal();
      fetchRestrictions(currentPage, searchTerm);
    } catch (error) {
      console.error("Error deleting restriction:", error);
      toast.error(error.response?.data?.message || "Failed to delete quantity restriction");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRestrictions.length === 0) {
      toast.warning("Please select at least one restriction to delete.");
      return;
    }

    if (window.confirm("Are you sure you want to delete the selected restrictions?")) {
      try {
        setLoading(true);
        // Delete each restriction individually
        for (const restrictionId of selectedRestrictions) {
          await axiosInstance.delete(`api/v1/quantity-restriction/admin/quantity-restriction/${restrictionId}`);
        }
        toast.success("Selected restrictions deleted successfully");
        setSelectedRestrictions([]);
        setShowBulkDelete(false);
        fetchRestrictions(currentPage, searchTerm);
      } catch (error) {
        console.error("Error deleting restrictions:", error);
        toast.error("Failed to delete selected restrictions");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleStatus = async (restrictionId) => {
    try {
      setLoading(true);
      await axiosInstance.put(`api/v1/quantity-restriction/admin/quantity-restriction/${restrictionId}/toggle`);
      toast.success("Restriction status updated successfully");
      fetchRestrictions(currentPage, searchTerm);
    } catch (error) {
      console.error("Error toggling restriction status:", error);
      toast.error("Failed to update restriction status");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProductNames = (productIds) => {
    return productIds.map(id => {
      return id.name
    }).join(", ");
  };

  const getProductOptions = () => {
    return products.map(product => ({
      value: product._id,
      label: product.name
    }));
  };

  const getSelectedProducts = (productIds) => {
    return productIds.map(id => {
      const product = products.find(p => p._id === id);
      return product ? { value: product._id, label: product.name } : null;
    }).filter(Boolean);
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Quantity Restrictions" breadcrumbItems={breadcrumbItems} />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <CardTitle className="h4">Quantity Restrictions List</CardTitle>
                    {searchTerm && (
                      <small className="text-muted">
                        Showing {restrictions.length} result(s) for "{searchTerm}"
                      </small>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <div className="d-flex gap-2" style={{ minWidth: "300px" }}>
                      <div className="position-relative flex-grow-1">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by name, description, or products..."
                          value={searchTerm}
                          onChange={handleSearch}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleSearchSubmit();
                            }
                          }}
                          style={{ paddingRight: searchTerm ? "40px" : "12px" }}
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            className="btn btn-link position-absolute"
                            style={{
                              right: "5px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              padding: "0",
                              color: "#6c757d",
                              border: "none",
                              background: "none",
                              fontSize: "16px"
                            }}
                            onClick={() => {
                              setSearchTerm("");
                              setRestrictions([]);
                              fetchRestrictions(1, "");
                            }}
                            title="Clear search"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <Button 
                        color="primary" 
                        onClick={handleSearchSubmit}
                        disabled={!searchTerm.trim()}
                      >
                        <i className="fas fa-search me-1"></i>
                        Search
                      </Button>
                    </div>
                    {showBulkDelete && (
                      <Button
                        color="danger"
                        onClick={handleBulkDelete}
                        className="me-2"
                      >
                        Bulk Delete
                      </Button>
                    )}
                    <Button color="primary" onClick={toggleModal}>
                      Add New Restriction
                    </Button>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={selectedRestrictions.length === restrictions.length && restrictions.length > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Max Quantity</th>
                        <th>Products</th>
                        <th>Valid Until</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {restrictions.map((restriction, index) => (
                        <tr
                          key={restriction._id}
                          className="restriction-row"
                          style={{
                            opacity: 0,
                            transform: "translateY(20px)",
                          }}
                          ref={(el) => {
                            if (el) {
                              gsap.to(el, {
                                opacity: 1,
                                y: 0,
                                duration: 0.1,
                                delay: index * 0.01,
                              });
                            }
                          }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedRestrictions.includes(restriction._id)}
                              onChange={() => handleCheckboxChange(restriction._id)}
                            />
                          </td>
                          <td>
                            <h5 className="font-size-14 mb-1">{restriction.name}</h5>
                          </td>
                          <td>
                            <p className="text-muted mb-0" style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {restriction.description}
                            </p>
                          </td>
                          <td>
                            <Badge color="info" className="px-2">
                              {restriction.maxQuantity}
                            </Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {restriction?.productIds?.length || 0} items
                            </small>
                          </td>
                          <td>
                            <span className={isExpired(restriction.validUntil) ? "text-danger" : "text-success"}>
                              {formatDate(restriction.validUntil)}
                            </span>
                          </td>
                          <td>
                            <Badge
                              color={restriction.isActive ? "success" : "danger"}
                              className="px-2"
                            >
                              {restriction.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => toggleEditModal(restriction)}
                              >
                                Edit
                              </Button>
                              <Button
                                color={restriction.isActive ? "warning" : "success"}
                                size="sm"
                                onClick={() => handleToggleStatus(restriction._id)}
                              >
                                {restriction.isActive ? "Deactivate" : "Activate"}
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => toggleDeleteModal(restriction)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {restrictions.length === 0 && !loading && (
                    <div className="text-center my-5">
                      <h4>No quantity restrictions found</h4>
                      <Button color="primary" className="mt-2" onClick={toggleModal}>
                        Add Your First Restriction
                      </Button>
                    </div>
                  )}
                </div>

                {loading && (
                  <div className="text-center my-4">
                    <Spinner color="primary" />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Create Modal */}
        <Modal isOpen={modal} toggle={toggleModal} size="lg">
          <ModalHeader toggle={toggleModal}>Create Quantity Restriction</ModalHeader>
          <Form onSubmit={handleSubmit}>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="name">Name *</Label>
                    <Input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      invalid={!!formErrors.name}
                    />
                    {formErrors.name && <FormText color="danger">{formErrors.name}</FormText>}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="maxQuantity">Max Quantity *</Label>
                    <Input
                      type="number"
                      name="maxQuantity"
                      id="maxQuantity"
                      value={formData.maxQuantity}
                      onChange={handleInputChange}
                      min="1"
                      invalid={!!formErrors.maxQuantity}
                    />
                    {formErrors.maxQuantity && <FormText color="danger">{formErrors.maxQuantity}</FormText>}
                  </FormGroup>
                </Col>
              </Row>
              
              <FormGroup>
                <Label for="description">Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </FormGroup>

              <FormGroup>
                <Label for="validUntil">Valid Until *</Label>
                <Input
                  type="datetime-local"
                  name="validUntil"
                  id="validUntil"
                  value={formData.validUntil}
                  onChange={handleInputChange}
                  invalid={!!formErrors.validUntil}
                />
                {formErrors.validUntil && <FormText color="danger">{formErrors.validUntil}</FormText>}
              </FormGroup>

              <FormGroup>
                <Label>Select Products *</Label>
                <Select
                  isMulti
                  name="products"
                  options={getProductOptions()}
                  value={getSelectedProducts(formData.productIds)}
                  onChange={(selectedOptions) => {
                    const productIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    setFormData(prev => ({
                      ...prev,
                      productIds: productIds
                    }));
                    if (formErrors.productIds) {
                      setFormErrors(prev => ({
                        ...prev,
                        productIds: ""
                      }));
                    }
                  }}
                  placeholder="Search and select products..."
                  isSearchable
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: '38px',
                      borderColor: formErrors.productIds ? '#dc3545' : state.isFocused ? '#80bdff' : '#ced4da',
                      boxShadow: state.isFocused ? (formErrors.productIds ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : '0 0 0 0.2rem rgba(0, 123, 255, 0.25)') : 'none',
                      '&:hover': {
                        borderColor: formErrors.productIds ? '#dc3545' : '#80bdff'
                      }
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#e3f2fd',
                      borderRadius: '4px'
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: '#1976d2',
                      fontSize: '13px'
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1976d2',
                        color: 'white'
                      }
                    })
                  }}
                />
                {formErrors.productIds && <FormText color="danger">{formErrors.productIds}</FormText>}
                {formData.productIds.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Selected: {formData.productIds.length} product(s)
                    </small>
                  </div>
                )}
              </FormGroup>

              <FormGroup check>
                <Input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                <Label check for="isActive">
                  Active
                </Label>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleModal}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Create"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* Edit Modal */}
        <Modal isOpen={editModal} toggle={toggleEditModal} size="lg">
          <ModalHeader toggle={toggleEditModal}>Edit Quantity Restriction</ModalHeader>
          <Form onSubmit={handleUpdate}>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="editName">Name *</Label>
                    <Input
                      type="text"
                      name="name"
                      id="editName"
                      value={formData.name}
                      onChange={handleInputChange}
                      invalid={!!formErrors.name}
                    />
                    {formErrors.name && <FormText color="danger">{formErrors.name}</FormText>}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="editMaxQuantity">Max Quantity *</Label>
                    <Input
                      type="number"
                      name="maxQuantity"
                      id="editMaxQuantity"
                      value={formData.maxQuantity}
                      onChange={handleInputChange}
                      min="1"
                      invalid={!!formErrors.maxQuantity}
                    />
                    {formErrors.maxQuantity && <FormText color="danger">{formErrors.maxQuantity}</FormText>}
                  </FormGroup>
                </Col>
              </Row>
              
              <FormGroup>
                <Label for="editDescription">Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  id="editDescription"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </FormGroup>

              <FormGroup>
                <Label for="editValidUntil">Valid Until *</Label>
                <Input
                  type="datetime-local"
                  name="validUntil"
                  id="editValidUntil"
                  value={formData.validUntil}
                  onChange={handleInputChange}
                  invalid={!!formErrors.validUntil}
                />
                {formErrors.validUntil && <FormText color="danger">{formErrors.validUntil}</FormText>}
              </FormGroup>

              <FormGroup>
                <Label>Select Products *</Label>
                <Select
                  isMulti
                  name="editProducts"
                  options={getProductOptions()}
                  value={getSelectedProducts(formData.productIds)}
                  onChange={(selectedOptions) => {
                    const productIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    setFormData(prev => ({
                      ...prev,
                      productIds: productIds
                    }));
                    if (formErrors.productIds) {
                      setFormErrors(prev => ({
                        ...prev,
                        productIds: ""
                      }));
                    }
                  }}
                  placeholder="Search and select products..."
                  isSearchable
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: '38px',
                      borderColor: formErrors.productIds ? '#dc3545' : state.isFocused ? '#80bdff' : '#ced4da',
                      boxShadow: state.isFocused ? (formErrors.productIds ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : '0 0 0 0.2rem rgba(0, 123, 255, 0.25)') : 'none',
                      '&:hover': {
                        borderColor: formErrors.productIds ? '#dc3545' : '#80bdff'
                      }
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#e3f2fd',
                      borderRadius: '4px'
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: '#1976d2',
                      fontSize: '13px'
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1976d2',
                        color: 'white'
                      }
                    })
                  }}
                />
                {formErrors.productIds && <FormText color="danger">{formErrors.productIds}</FormText>}
                {formData.productIds.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Selected: {formData.productIds.length} product(s)
                    </small>
                  </div>
                )}
              </FormGroup>

              <FormGroup check>
                <Input
                  type="checkbox"
                  name="isActive"
                  id="editIsActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                <Label check for="editIsActive">
                  Active
                </Label>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleEditModal}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Update"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
          <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
          <ModalBody>
            <Alert color="warning">
              Are you sure you want to delete the quantity restriction "{selectedRestriction?.name}"? 
              This action cannot be undone and will also delete all associated user tracking data.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleDeleteModal}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDelete} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Delete"}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default QuantityRestriction;
